import type { ActivitySource, ActivityViewData } from "../types";
import type { DatabaseAdapter } from "@rapid/providers/database/DatabaseAdapter";
import { convertTask, type FirestoreTask } from "./asyncTaskTypes";

/**
 * ActivitySource backed by the `async_tasks` Firestore collection.
 *
 * Listens to top-level tasks (parentTaskId == null) created in the last 7 days
 * for a given workspace, converts each Firestore document into an ActivityViewData,
 * and pushes updates in real-time via onSnapshot.
 *
 * Only tasks registered in the taskTypes registry (see asyncTaskTypes.ts) are
 * surfaced. Unregistered task types are silently filtered out.
 */
export class AsyncTaskService implements ActivitySource {
    private workspaceId: string;
    private database: DatabaseAdapter;

    constructor(workspaceId: string, database: DatabaseAdapter) {
        this.workspaceId = workspaceId;
        this.database = database;
    }

    subscribe(callback: (activities: ActivityViewData[]) => void): () => void {
        if (!this.workspaceId) {
            callback([]);
            return () => {};
        }

        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        return this.database.onQuerySnapshot(
            "async_tasks",
            {
                where: [
                    ["owner", "==", this.workspaceId],
                    ["parentTaskId", "==", null],
                    ["createdAt", ">=", sevenDaysAgo]
                ],
                orderBy: ["createdAt", "desc"],
                limit: 50
            },
            (docs) => {
                const activities: ActivityViewData[] = [];
                for (const doc of docs) {
                    const task = { ...doc.data(), id: doc.id } as FirestoreTask;
                    const converted = convertTask(task);
                    if (converted) activities.push(converted);
                }
                callback(activities);
            },
            (error) => {
                // eslint-disable-next-line no-console
                console.error("AsyncTaskService onSnapshot error:", error);
                callback([]);
            }
        );
    }
}
